/**
 * Reputation and Trust Score System
 * Calculates user reputation based on community contributions
 */

// User Level Thresholds
export const USER_LEVELS = {
  1: { name: 'New Member', minScore: 0, color: 'text-gray-400', icon: '🌱' },
  2: { name: 'Active Contributor', minScore: 50, color: 'text-blue-400', icon: '⭐' },
  3: { name: 'Trusted Reporter', minScore: 150, color: 'text-purple-400', icon: '🛡️' },
  4: { name: 'Expert Verifier', minScore: 300, color: 'text-yellow-400', icon: '👑' },
  5: { name: 'Guardian', minScore: 600, color: 'text-orange-400', icon: '🔥' },
  6: { name: 'Legend', minScore: 1000, color: 'text-red-400', icon: '💎' }
};

// Achievement Badges
export const ACHIEVEMENTS = {
  FIRST_POST: {
    id: 'first_post',
    name: 'First Report',
    description: 'Submitted your first fraud report',
    icon: '📝',
    points: 10
  },
  VERIFIED_REPORTER: {
    id: 'verified_reporter',
    name: 'Verified Reporter',
    description: 'Had a report verified by the community',
    icon: '✅',
    points: 25
  },
  ACTIVE_VERIFIER: {
    id: 'active_verifier',
    name: 'Active Verifier',
    description: 'Verified 10+ reports',
    icon: '🔍',
    points: 50
  },
  POPULAR_AUTHOR: {
    id: 'popular_author',
    name: 'Popular Author',
    description: 'Received 50+ total likes',
    icon: '❤️',
    points: 30
  },
  CONVERSATION_STARTER: {
    id: 'conversation_starter',
    name: 'Conversation Starter',
    description: 'Started 10+ discussions in channels',
    icon: '💬',
    points: 20
  },
  COMMUNITY_HELPER: {
    id: 'community_helper',
    name: 'Community Helper',
    description: 'Posted 100+ comments',
    icon: '🤝',
    points: 40
  },
  EARLY_ADOPTER: {
    id: 'early_adopter',
    name: 'Early Adopter',
    description: 'Joined in the first 1000 members',
    icon: '🚀',
    points: 50
  }
};

/**
 * Calculate user reputation score based on multiple factors
 * @param {Object} userData - User statistics
 * @returns {Object} Reputation breakdown and total score
 */
export function calculateReputationScore(userData) {
  const {
    totalPosts = 0,
    verifiedPosts = 0,
    totalLikes = 0,
    totalComments = 0,
    verificationsPerformed = 0,
    accurateVerifications = 0,
    channelMessages = 0,
    accountAge = 0, // in days
    achievements = []
  } = userData;

  const scores = {
    // Base activity scores
    postScore: totalPosts * 5,
    verifiedPostBonus: verifiedPosts * 15,
    likeScore: totalLikes * 2,
    commentScore: totalComments * 1,

    // Verifier scores (higher weight)
    verificationScore: verificationsPerformed * 8,
    accuracyBonus: accurateVerifications * 12,

    // Community engagement
    channelActivityScore: Math.min(channelMessages * 0.5, 50), // Cap at 50

    // Longevity bonus
    accountAgeBonus: Math.min(accountAge * 0.2, 30), // Cap at 30

    // Achievement bonuses
    achievementScore: achievements.reduce((sum, achId) => {
      const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achId);
      return sum + (achievement?.points || 0);
    }, 0)
  };

  // Calculate trust multiplier (0.8 to 1.2)
  const verificationAccuracy = verificationsPerformed > 0
    ? accurateVerifications / verificationsPerformed
    : 1;
  const trustMultiplier = 0.8 + (verificationAccuracy * 0.4);

  const baseScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
  const finalScore = Math.round(baseScore * trustMultiplier);

  return {
    scores,
    trustMultiplier,
    baseScore,
    finalScore
  };
}

/**
 * Get user level based on reputation score
 * @param {number} score - Reputation score
 * @returns {Object} User level information
 */
export function getUserLevel(score) {
  const levels = Object.values(USER_LEVELS).reverse();
  const level = levels.find(l => score >= l.minScore) || USER_LEVELS[1];
  return level;
}

/**
 * Check which achievements a user has earned
 * @param {Object} userData - User statistics
 * @returns {Array} Array of earned achievement IDs
 */
export function checkAchievements(userData) {
  const {
    totalPosts = 0,
    verifiedPosts = 0,
    totalLikes = 0,
    totalComments = 0,
    verificationsPerformed = 0,
    channelMessages = 0,
    userId = '',
  } = userData;

  const earned = [];

  // Check each achievement
  if (totalPosts >= 1) earned.push(ACHIEVEMENTS.FIRST_POST.id);
  if (verifiedPosts >= 1) earned.push(ACHIEVEMENTS.VERIFIED_REPORTER.id);
  if (verificationsPerformed >= 10) earned.push(ACHIEVEMENTS.ACTIVE_VERIFIER.id);
  if (totalLikes >= 50) earned.push(ACHIEVEMENTS.POPULAR_AUTHOR.id);
  if (channelMessages >= 10) earned.push(ACHIEVEMENTS.CONVERSATION_STARTER.id);
  if (totalComments >= 100) earned.push(ACHIEVEMENTS.COMMUNITY_HELPER.id);

  return earned;
}

/**
 * Calculate next level progress
 * @param {number} currentScore - Current reputation score
 * @returns {Object} Progress information
 */
export function getNextLevelProgress(currentScore) {
  const currentLevel = getUserLevel(currentScore);
  const levelEntries = Object.entries(USER_LEVELS);
  const currentLevelIndex = levelEntries.findIndex(([_, level]) => level.name === currentLevel.name);

  if (currentLevelIndex === levelEntries.length - 1) {
    // Max level
    return {
      nextLevel: null,
      pointsNeeded: 0,
      progressPercentage: 100
    };
  }

  const nextLevelEntry = levelEntries[currentLevelIndex + 1];
  const nextLevel = nextLevelEntry[1];
  const pointsNeeded = nextLevel.minScore - currentScore;
  const pointsInCurrentLevel = currentScore - currentLevel.minScore;
  const pointsRequiredForNextLevel = nextLevel.minScore - currentLevel.minScore;
  const progressPercentage = Math.round((pointsInCurrentLevel / pointsRequiredForNextLevel) * 100);

  return {
    nextLevel,
    pointsNeeded,
    progressPercentage: Math.min(progressPercentage, 100)
  };
}

/**
 * Get trust badge color based on verification accuracy
 * @param {number} accuracy - Verification accuracy (0-1)
 * @returns {string} Tailwind color class
 */
export function getTrustBadgeColor(accuracy) {
  if (accuracy >= 0.9) return 'text-green-400 border-green-500/40';
  if (accuracy >= 0.75) return 'text-blue-400 border-blue-500/40';
  if (accuracy >= 0.6) return 'text-yellow-400 border-yellow-500/40';
  return 'text-red-400 border-red-500/40';
}

/**
 * Format reputation score for display
 * @param {number} score - Reputation score
 * @returns {string} Formatted score
 */
export function formatReputationScore(score) {
  if (score >= 1000000) return `${(score / 1000000).toFixed(1)}M`;
  if (score >= 1000) return `${(score / 1000).toFixed(1)}K`;
  return score.toString();
}
